// import { AUDIO_PATHS } from "../audio/audio_constants.js";
import { Screens } from "../screen_manager/screen_manager.js";
import { checkStorageCapacity, estimateTotalBytes } from "../utils/storage_utils.js";
import { ImgID } from "./img_id.js";
import { NetworkManager } from "../network_manager.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
// import { DevToDev } from "../plugins/dev_to_dev_plugin.js";

interface ImgDescriptor
{
  id: ImgID;
  url: string;
  screens: Screens[];
  deferred: boolean;
}

type Progress = { loaded: number; total: number };

export class ImagesHolder
{
  static TOTAL_COUNT: number = 0;
  static LOADED_COUNT: number = 0;

  private static descriptors: ImgDescriptor[] = [];

  private static images_map: Map<ImgID, ImageBitmap> = new Map();
  private static inflight = new Map<ImgID, Promise<ImageBitmap>>();
  private static kept_blobs = new Map<ImgID, Blob>();
  private static load_waiters = new Map<ImgID, Set<( bmp: ImageBitmap ) => void>>();
  private static failed_at = new Map<ImgID, number>();
  private static online_hooked = false;
  private static readonly MISSING_RETRY_MS = 2000;

  private static screen_progress = new Map<Screens, Progress>();
  private static screen_listeners = new Map<Screens, Set<( p: Progress ) => void>>();

  private static downloaded_bytes: number = 0;
  private static load_start_ts: number = 0;

  private static COMMON_DIR_HINTS = ['common'];

  public static is_loading_finished: boolean = true;

  // Optional UI timing clamp for loading progress visualization
  private static MIN_DURATION_START: number = 0;
  private static MIN_DURATION_END: number = 0;

  public static setMinDurationWindow(start: number, duration_ms: number): void
  {
    this.MIN_DURATION_START = start;
    this.MIN_DURATION_END   = start + Math.max( 0, duration_ms );
  }

  public static clearMinDurationWindow(): void
  {
    this.MIN_DURATION_START = 0;
    this.MIN_DURATION_END   = 0;
  }

  public static getMinDurationWindow(): { start: number; end: number } | null
  {
    if ( this.MIN_DURATION_END <= this.MIN_DURATION_START || this.MIN_DURATION_END === 0 )
      return null;
    return { start: this.MIN_DURATION_START, end: this.MIN_DURATION_END };
  }

  public static addDescriptor( id: ImgID, url: string, auto_screens?: Screens[] )
  {
    const screens = auto_screens ?? this.findImageScreens( url );

    this.descriptors.push( { id, url, screens, deferred: this.isDeferredUrl( url ) } );
  }

  private static DEFERRED_URL_HINTS = ['assets/art/flags/'];

  private static isDeferredUrl( url: string ): boolean
  {
    return this.DEFERRED_URL_HINTS.some( hint => url.includes( hint ) );
  }

  public static isReady( id: ImgID ): boolean
  {
    return this.images_map.has( id );
  }

  private static deferred_promise: Promise<void> | null = null;

  public static preloadDeferred( batch_size: number = 16 ): Promise<void>
  {
    if ( this.deferred_promise )
      return this.deferred_promise;

    this.deferred_promise = ( async () =>
    {
      const need = this.descriptors.filter( d => d.deferred && !this.images_map.has( d.id ) );
      need.sort( ( a, b ) => Number( b.url.includes( '_unknown' ) ) - Number( a.url.includes( '_unknown' ) ) );

      await this.runPool( need, batch_size, async d =>
      {
        try { await this.retryEnsure( d.id ); }
        catch ( err ) { console.error( `Deferred image id ${d.id} loading error:`, err ); }

        await ResourceManager.pacingDelay();
      } );
    } )();

    return this.deferred_promise;
  }

  private static normalizeToken( s: string ): string
  {
    return s.toLowerCase().replace( /[^a-z0-9]/g, "" );
  }

  private static async runPool<T>(
    items: T[],
    limit: number,
    worker: ( item: T ) => Promise<void>
  ): Promise<void>
  {
    const width = Math.max( 1, Math.min( limit, items.length ) );
    let next = 0;

    const runner = async (): Promise<void> =>
    {
      for ( let i = next++; i < items.length; i = next++ )
        await worker( items[i] );
    };

    await Promise.all( Array.from( { length: width }, runner ) );
  }

  public static async preloadAll( batch_size: number = 10 ): Promise<void>
  {
    this.is_loading_finished = false;
    this.load_start_ts = performance.now();
    const list = this.descriptors.filter( d => !d.deferred );

    this.TOTAL_COUNT = list.length;
    // Baseline already-loaded images
    this.LOADED_COUNT = list.filter( d => this.images_map.has( d.id ) ).length;

    const need = list.filter( d => !this.images_map.has( d.id ) && !d.deferred );

    await this.runPool( need, batch_size, async d =>
    {
      try
      {
        await this.retryEnsure( d.id );
        this.LOADED_COUNT += 1;
      } catch ( err )
      {
        console.error( `Image id ${d.id} loading error:`, err );
      }
    } );

    await this.ensureAllLoaded();
    this.is_loading_finished = true;
    try {
      const dt = Math.round(Math.max(0, performance.now() - this.load_start_ts));
      // void DevToDev.sendEvent(`assets_loaded_count_${this.LOADED_COUNT}_total_${this.TOTAL_COUNT}_time_${dt}ms`);
    } catch {}
  }

  public static async preloadImages( ids: ImgID[], batch_size: number = 10 ): Promise<void>
  {
    this.is_loading_finished = false;
    this.load_start_ts = performance.now();
    const list = this.descriptors.filter( d => ids.includes( d.id ) );

    // Keep counters accurate for partial preloads
    this.TOTAL_COUNT = list.length;
    this.LOADED_COUNT = list.filter( d => this.images_map.has( d.id ) ).length;

    const need = list.filter( d => !this.images_map.has( d.id ) );

    for ( let i = 0; i < need.length; i += batch_size )
    {
      const batch = need.slice( i, i + batch_size );

      await Promise.all(
        batch.map( async d =>
        {
          try
          {
            await this.retryEnsure( d.id );
            this.LOADED_COUNT += 1;
          } catch ( err )
          {
            console.error( `Image id ${d.id} loading error:`, err );
          }
        } )
      );
    }
    // await this.ensureAllLoaded();
    this.is_loading_finished = true;
    try {
      const dt = Math.round(Math.max(0, performance.now() - this.load_start_ts));
      // void DevToDev.sendEvent(`assets_loaded_count_${this.LOADED_COUNT}_total_${this.TOTAL_COUNT}_time_${dt}ms`);
    } catch {}
  }

  private static findImageScreens( url: string ): Screens[]
  {
    const parts = url.split( /[\\/]/ );
    const dirs = parts.slice( 0, -1 );
    const dirs_norm = dirs.map( this.normalizeToken );

    if ( dirs_norm.some( p => this.COMMON_DIR_HINTS.includes( p ) ) ) return [];

    const found = new Set<Screens>();

    for ( const screen of Object.values( Screens ) )
    {
      const screen_key = this.normalizeToken( String( screen ) );

      const hit = dirs_norm.some( dir => dir === screen_key + "screen" );
      if ( hit ) found.add( screen );
    }

    return Array.from( found );
  }

  private static notify( screen: Screens ): void
  {
    const prog = this.screen_progress.get( screen );
    if ( !prog ) return;
    const set = this.screen_listeners.get( screen );
    if ( !set ) return;
    for ( const fn of set ) fn( prog );
  }

  public static async preloadScreens(
    screens: Screens[],
    batch_size: number = 10,
    preload_important: boolean = false
  ): Promise<void>
  {
    this.is_loading_finished = false;
    const target = new Set<Screens>( screens );
    const all = this.descriptors.filter( d => d.screens.some( s => target.has( s ) ) );

    if ( preload_important )
    {
      const important_list = this.descriptors.filter(
        d => d.screens.length === 0 && !this.images_map.has( d.id ) && !this.inflight.has( d.id )
      );

      all.push( ...important_list );
    }

    const uniq_by_id = new Map<ImgID, ImgDescriptor>();

    for ( const descr of all )
      uniq_by_id.set( descr.id, descr );

    const list = Array.from( uniq_by_id.values() );

    this.TOTAL_COUNT = list.length;
    this.LOADED_COUNT = 0;

    const per_screen_totals = new Map<Screens, number>();
    const per_screen_loaded = new Map<Screens, number>();

    for ( const s of target )
    {
      const imgs_for_screen = list.filter( d => d.screens.includes( s ) );
      const total = imgs_for_screen.length;
      const loaded = imgs_for_screen.filter( d => this.images_map.has( d.id ) ).length;

      per_screen_totals.set( s, total );
      per_screen_loaded.set( s, loaded );
      this.screen_progress.set( s, { loaded, total } );

      this.notify( s );
    }

    this.LOADED_COUNT += list.filter( d => this.images_map.has( d.id ) ).length;

    const need = list.filter( d => !this.images_map.has( d.id ) );

    for ( let i = 0; i < need.length; i += batch_size )
    {
      const batch = need.slice( i, i + batch_size );

      await Promise.all( batch.map( async d =>
      {
        try
        {
          await this.retryEnsure( d.id );

          this.LOADED_COUNT += 1;

          for ( const s of d.screens )
          {
            if ( !target.has( s ) )
              continue;
            const total = per_screen_totals.get( s ) ?? 0;

            if ( total > 0 )
            {
              const prev = per_screen_loaded.get( s ) ?? 0;
              const next = Math.min( prev + 1, total );
              per_screen_loaded.set( s, next );
              this.screen_progress.set( s, { loaded: next, total } );
              this.notify( s );
            }
          }
        } catch ( err )
        {
          console.error( `Image id ${d.id} loading error:`, err );
        }
      } ) );
    }
    await this.ensureAllLoaded( 2, 300, screens );
    this.is_loading_finished = true;
  }

  private static RELEASE_MIN_BYTES = 524288;

  public static releaseScreens( keep: Screens[] ): void
  {
    const keep_set = new Set( keep );
    let released = 0;
    let freed = 0;

    for ( const d of this.descriptors )
    {
      if ( d.screens.length === 0 || d.screens.some( s => keep_set.has( s ) ) )
        continue;

      const bmp = this.images_map.get( d.id );
      if ( !bmp )
        continue;

      const bytes = bmp.width * bmp.height * 4;
      if ( bytes < ImagesHolder.RELEASE_MIN_BYTES )
        continue;

      this.images_map.delete( d.id );
      this.gpu_warmed.delete( d.id );
      released++;
      freed += bytes;
    }

    if ( released > 0 )
      console.log( `[ImagesHolder] released ${released} images (~${( freed / 1048576 ).toFixed( 1 )}MB), keeping [${keep.join( ", " )}]` );
  }

  private static async fetchAndDecode( d: ImgDescriptor ): Promise<ImageBitmap>
  {
    const kept = this.kept_blobs.get( d.id );
    if ( kept )
    {
      try
      {
        return this.store( d.id, await createImageBitmap( kept ) );
      }
      catch
      {
        this.kept_blobs.delete( d.id );
      }
    }

    let resp: Response;
    try
    {
      resp = await fetch( d.url );
    }
    catch ( error )
    {
      NetworkManager.handleNetworkError( error );
      throw error;
    }

    if ( !resp.ok )
    {
      const error = new Error( `Image ${d.url} loading error: ${resp.status} ${resp.statusText}` );
      NetworkManager.handleLoadingDataError( error );
      throw error;
    }

    let blob: Blob;
    try
    {
      blob = await resp.blob();
    }
    catch ( error )
    {
      NetworkManager.handleNetworkError( error );
      throw error;
    }
    this.downloaded_bytes += blob.size;

    let bmp: ImageBitmap;
    try
    {
      bmp = await createImageBitmap( blob );
    }
    catch ( error )
    {
      NetworkManager.handleLoadingDataError( error );
      throw error;
    }
    if ( d.screens.length > 0 && bmp.width * bmp.height * 4 >= ImagesHolder.RELEASE_MIN_BYTES )
      this.kept_blobs.set( d.id, blob );

    return this.store( d.id, bmp );
  }

  private static images_version = 0;
  private static placeholder_draws = 0;

  public static imagesVersion(): number { return this.images_version; }

  public static placeholderDraws(): number { return this.placeholder_draws; }

  private static store( id: ImgID, bmp: ImageBitmap ): ImageBitmap
  {
    this.images_version++;
    this.images_map.set( id, bmp );
    this.failed_at.delete( id );
    const waiters = this.load_waiters.get( id );
    if ( waiters )
    {
      this.load_waiters.delete( id );
      for ( const waiter of waiters )
      {
        try { waiter( bmp ); }
        catch ( e ) { console.warn( `[ImagesHolder] load waiter for ${id} failed`, e ); }
      }
    }
    return bmp;
  }

  public static isPlaceholder( image: CanvasImageSource | null | undefined ): boolean
  {
    return !!image && image === ( this.placeholder_canvas as unknown );
  }

  public static whenLoaded( id: ImgID, callback: ( bmp: ImageBitmap ) => void ): void
  {
    const loaded = this.images_map.get( id );
    if ( loaded )
    {
      callback( loaded );
      return;
    }
    let waiters = this.load_waiters.get( id );
    if ( !waiters )
    {
      waiters = new Set();
      this.load_waiters.set( id, waiters );
    }
    waiters.add( callback );
    this.hookOnline();
  }

  private static hookOnline(): void
  {
    if ( this.online_hooked )
      return;
    this.online_hooked = true;
    NetworkManager.addOnlineListener( () =>
    {
      for ( const id of [ ...this.load_waiters.keys() ] )
      {
        this.failed_at.delete( id );
        this.requestMissing( id );
      }
    } );
  }

  private static requestMissing( id: ImgID ): void
  {
    if ( this.inflight.has( id ) )
      return;
    const failed = this.failed_at.get( id );
    if ( failed !== undefined && performance.now() - failed < ImagesHolder.MISSING_RETRY_MS )
      return;
    this.ensure( id ).catch( () => { this.failed_at.set( id, performance.now() ); } );
  }

  private static gpu_warmup_ctx: CanvasRenderingContext2D | null = null;
  private static gpu_warmed = new Set<ImgID>();

  public static async warmupGpu( ids: ImgID[] ): Promise<void>
  {
    if ( !this.gpu_warmup_ctx )
    {
      const canvas = document.createElement( "canvas" );
      canvas.width = 512;
      canvas.height = 512;
      this.gpu_warmup_ctx = canvas.getContext( "2d" );
    }

    const ctx = this.gpu_warmup_ctx;
    if ( !ctx )
      return;

    for ( const id of ids )
    {
      if ( this.gpu_warmed.has( id ) )
        continue;

      const bmp = this.images_map.get( id );
      if ( bmp )
      {
        ctx.drawImage( bmp, 0, 0, 8, 8 );
        this.gpu_warmed.add( id );
      }

      await ResourceManager.pacingDelay();
    }
  }

  public static ensure( id: ImgID ): Promise<ImageBitmap>
  {
    const d = this.descriptors.find( x => x.id === id );
    if ( !d )
      return Promise.reject( new Error( `Descriptor for ${id} not found` ) );

    const cached = this.images_map.get( id );
    if ( cached )
      return Promise.resolve( cached );

    const pending = this.inflight.get( id );
    if ( pending )
      return pending;

    const task = this.fetchAndDecode( d ).finally( () => this.inflight.delete( id ) );
    this.inflight.set( id, task );

    return task;
  }

  private static placeholder_canvas: HTMLCanvasElement | null = null;

  public static getLoadedImage( id: ImgID ): ImageBitmap | null
  {
    return this.images_map.get( id ) ?? null;
  }

  public static getImage( id: ImgID ): ImageBitmap
  {
    const res: ImageBitmap | undefined = this.images_map.get( id );
    if ( !res )
    {
      this.placeholder_draws++;
      if ( !this.load_waiters.has( id ) && !this.inflight.has( id ) && !this.failed_at.has( id ) )
        console.error( `Image id ${id} not loaded — drawing placeholder, reloading in background` );
      this.requestMissing( id );
      this.hookOnline();

      if ( !this.placeholder_canvas )
      {
        this.placeholder_canvas = document.createElement( "canvas" );
        this.placeholder_canvas.width = 1;
        this.placeholder_canvas.height = 1;
      }
      return this.placeholder_canvas as unknown as ImageBitmap;
    }

    return res;
  }

  public static isLoadingFinished() { return this.is_loading_finished; }

  public static getAllImageUrls(): string[]
  {
    return this.descriptors.map( d => d.url );
  }

  public static getMissingImageIds( screens?: Screens[] ): ImgID[]
  {
    return this.descriptors
      .filter( d => !d.deferred && !this.images_map.has( d.id ) )
      .filter( d => !screens || d.screens.length === 0 || d.screens.some( s => screens.includes( s ) ) )
      .map( d => d.id );
  }

  private static async retryEnsure( id: ImgID, retries: number = 2, delayMs: number = 300 ): Promise<void>
  {
    let lastErr: any;
    for ( let attempt = 0; attempt <= retries; attempt++ )
    {
      try
      {
        await this.ensure( id );
        return;
      }
      catch ( err )
      {
        lastErr = err;
        if ( !NetworkManager.isOnline() && !NetworkManager.isOfflinePlayable() )
          await NetworkManager.waitForOnline();
        if ( attempt < retries )
          await new Promise( r => setTimeout( r, delayMs * ( attempt + 1 ) ) );
      }
    }
    throw lastErr;
  }

  public static getDownloadedBytes(): number { return this.downloaded_bytes; }

  public static async ensureAllLoaded( retries: number = 2, delayMs: number = 300, screens?: Screens[] ): Promise<{ failed: ImgID[] }>
  {
    const missing = this.getMissingImageIds( screens );
    if ( missing.length === 0 ) return { failed: [] };

    const failed: ImgID[] = [];
    await Promise.all( missing.map( async id =>
    {
      try { await this.retryEnsure( id, retries, delayMs ); }
      catch { failed.push( id ); }
    } ) );
    return { failed };
  }

  public static async estimateImagesBytes( include_loaded: boolean = true ): Promise<number>
  {
    const urls = include_loaded
      ? this.descriptors.map( d => d.url )
      : this.descriptors.filter( d => !this.images_map.has( d.id ) ).map( d => d.url );
    const { total } = await estimateTotalBytes( urls );
    return total;
  }

  public static async checkDeviceStorageForResources( include_audio: boolean = true, include_loaded_images: boolean = true )
  {
    const image_urls = include_loaded_images
      ? this.descriptors.map( d => d.url )
      : this.descriptors.filter( d => !this.images_map.has( d.id ) ).map( d => d.url );

    // const audio_urls = include_audio ? Object.values( AUDIO_PATHS ) : [];
    const audio_urls: any[] = [];
    const all_urls = [...image_urls, ...audio_urls];
    const { total } = await estimateTotalBytes( all_urls );
    return await checkStorageCapacity( total );
  }

  public static async assertEnoughDeviceStorageForResources( include_audio: boolean = true, include_loaded_images: boolean = true ): Promise<void>
  {
    const res = await this.checkDeviceStorageForResources( include_audio, include_loaded_images );
    if ( !res.ok )
    {
      const needMb = ( res.needed / ( 1024 * 1024 ) ).toFixed( 2 );
      const freeMb = ( ( ( res.quota - res.usage ) / ( 1024 * 1024 ) ) || 0 ).toFixed( 2 );
      throw new Error( `Not enough device storage for resources. Needed: ${needMb} MB, free: ${freeMb} MB` );
    }
  }
}
